import {FC, useCallback, useState} from 'react'
import {useQuery} from 'react-query'
import styled from 'styled-components'

import {ServiceNoticeQueries} from '../../api/service-notice'
import {
  dismissServiceNotice,
  getServiceNoticeDismissKey,
  isServiceNoticeActive,
  isServiceNoticeDismissed,
  pickLocalizedText,
} from '../../api/service-notice/helpers'
import {ServiceNoticeSeverity} from '../../api/service-notice/types'
import {theme} from '../../theme/theme'
import {ExsportiaIcon} from '../../ui/components/exsportia-icon/exsportia-icon'
import {useLocale} from '../../utils/i18n'

type SeverityStyle = {
  accent: keyof typeof theme.colors
  background: keyof typeof theme.colors
  icon: string
}

const severityStyles: Record<ServiceNoticeSeverity, SeverityStyle> = {
  info: {accent: 'blue', background: 'blue_halftone', icon: 'info'},
  warning: {accent: 'notify', background: 'yellow_halftone', icon: 'warning'},
  critical: {accent: 'error', background: 'red_halftone', icon: 'error'},
}

const Wrapper = styled.div<{severity: ServiceNoticeSeverity}>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: ${theme.colors.bg};
  background-image: linear-gradient(
    ${({severity}) => theme.colors[severityStyles[severity].background]},
    ${({severity}) => theme.colors[severityStyles[severity].background]}
  );
  border-bottom: 2px solid ${({severity}) => theme.colors[severityStyles[severity].accent]};
  color: ${theme.colors['text-default']};
  font-size: ${theme.fontSizes.body};
  line-height: 1.4;
`

const IconBox = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  padding-top: 1px;
`

const Content = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`

const Title = styled.strong`
  font-weight: ${theme.fontWeights.bold};
`

const Message = styled.span`
  white-space: pre-line;
`

const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
  }
`

export const ServiceNoticeBanner: FC = () => {
  const {l, language} = useLocale()
  const {data: notice} = useQuery(ServiceNoticeQueries.getServiceNoticeQuery({}))
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)
  const dismissKey = notice ? getServiceNoticeDismissKey(notice) : null

  const onDismiss = useCallback(() => {
    if (dismissKey) {
      dismissServiceNotice(dismissKey)
      setDismissedKey(dismissKey)
    }
  }, [dismissKey])

  if (
    !notice
    || !dismissKey
    || dismissedKey === dismissKey
    || isServiceNoticeDismissed(dismissKey)
    || !isServiceNoticeActive(notice)
  ) {
    return null
  }

  const title = pickLocalizedText(notice.title, language)
  const message = pickLocalizedText(notice.message, language)
  if (!title && !message) {
    return null
  }
  const style = severityStyles[notice.severity]

  return (
    <Wrapper
      severity={notice.severity}
      role={notice.severity === 'critical' ? 'alert' : 'status'}
      aria-label={l('service-notice-label')}
      data-testid='service-notice-banner'
    >
      <IconBox aria-hidden>
        <ExsportiaIcon
          icon={style.icon}
          color={style.accent}
        />
      </IconBox>
      <Content>
        {title && <Title>{title}</Title>}
        {title && message && ' '}
        {message && <Message>{message}</Message>}
      </Content>
      <DismissButton
        type='button'
        onClick={onDismiss}
        aria-label={l('service-notice-dismiss')}
        title={l('service-notice-dismiss')}
      >
        <ExsportiaIcon
          icon='close'
          color='text-secondary'
        />
      </DismissButton>
    </Wrapper>
  )
}
